// Result rows → decoded map features (T13, shared with T14). ASYNC-ONLY: every value routes through
// `decodeSpatialBatch` (the eager-safe loader) with an AbortSignal cancelled when inputs change — the
// sync decode core is NEVER imported here, so maplibre/wkx stay in the lazy chunk. Values over
// `MAX_EAGER_DECODE_BYTES` are not auto-decoded (counted as too-large). Feature `properties` carry
// ONLY non-spatial columns with values ≤ 1 KB — geometry and large blobs are excluded so a 10k-feature
// map never duplicates whole rows into MapLibre memory.

import { useEffect, useState } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialMapFeature } from '../map/spatialMapTypes';
import { decodeSpatialBatch, type SpatialBatchItem } from '../decode/decodeSpatialBatch';
import { MAX_EAGER_DECODE_BYTES } from '../decode/decodeLimits';
import {
  resolveSpatialCellInput,
  type SpatialAccessorContext,
  type SpatialColumnDeclaration,
} from '../../common/utils/spatialValueAccessor';

const MAX_PROPERTY_BYTES = 1024;

/**
 * `declaration` (Task 18) makes the geometry column a MetaInfo-declared coordinate column
 * (WKT/GeoJSON/latlng-text single column, or a lat-lng pair). When present, `columnId` is the
 * lat/single column and every feature resolves through the accessor's declared arm — a pair
 * assembles `{ lat, lng }` from BOTH columns and routes to `decodeDeclaredSpatialValue`.
 */
export type GeometryRef = {
  columnId: string;
  dataType?: string;
  declaration?: SpatialColumnDeclaration;
};

export type ResultSetFeatures = {
  features: SpatialMapFeature[];
  truncated: boolean;
  total: number;
  failedCount: number;
  /** Subset of `failedCount`: values over `MAX_EAGER_DECODE_BYTES` (banner: "N too large to map"). */
  tooLargeCount: number;
  /** Rows whose geometry cell was NULL/absent (no feature, not a failure). */
  nullCount: number;
  loading: boolean;
};

const EMPTY: ResultSetFeatures = {
  features: [],
  truncated: false,
  total: 0,
  failedCount: 0,
  tooLargeCount: 0,
  nullCount: 0,
  loading: false,
};

/** ≤ 1 KB proxy: char length is a cheap, safe upper-ish bound for the property-attachment cap. */
function withinPropertyCap(value: unknown): boolean {
  const s = typeof value === 'string' ? value : String(value);
  return s.length <= MAX_PROPERTY_BYTES;
}

function buildProperties(
  row: Record<string, unknown>,
  attachColumnIds: string[],
): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const id of attachColumnIds) {
    const value = row[id];
    if (value === null || value === undefined) continue;
    if (!withinPropertyCap(value)) continue;
    props[id] = value;
  }
  return props;
}

export function useResultSetFeatures(
  rows: Record<string, unknown>[],
  engine: DatabaseEngineId | null | undefined,
  geometryRef: GeometryRef | null,
  cap: number,
  options?: { attachColumnIds?: string[] },
): ResultSetFeatures {
  const attachColumnIds = options?.attachColumnIds ?? [];
  const attachKey = attachColumnIds.join(' ');
  const geomId = geometryRef?.columnId ?? null;
  const geomType = geometryRef?.dataType;
  const declaration = geometryRef?.declaration;
  // Stable dep key for the declaration (its identity is (id, format, lngColumn, axisOrder)).
  const declKey = declaration
    ? `${declaration.id}:${declaration.format}:${declaration.lngColumn ?? ''}:${declaration.axisOrder ?? ''}`
    : '';

  const [state, setState] = useState<ResultSetFeatures>({ ...EMPTY, loading: true });

  useEffect(() => {
    if (!engine || !geomId) {
      setState({ ...EMPTY, loading: false });
      return;
    }

    const capped = rows.length > cap ? rows.slice(0, cap) : rows;
    const truncated = rows.length > cap;
    // Declared arm (T18): the accessor assembles the geometry from the declaration (a pair pulls
    // BOTH columns from the row); native arm reads the single geometry column.
    const ctx: SpatialAccessorContext = {
      engine,
      nativeSpatialColumnIds: declaration ? new Set() : new Set([geomId]),
      columnDataTypes: new Map([[geomId, geomType ?? '']]),
      declaredSpatialColumns: declaration ? new Map([[geomId, declaration]]) : undefined,
    };

    const items: SpatialBatchItem[] = [];
    let nullCount = 0;
    capped.forEach((row, i) => {
      const input = resolveSpatialCellInput(geomId, row, ctx);
      // A declared pair carries `{ lat, lng }`; treat an all-null pair as a null cell.
      const pairAllNull =
        input?.declaration?.format === 'lat-lng-pair' &&
        (() => {
          const v = input.value as { lat?: unknown; lng?: unknown } | null;
          return !v || (v.lat == null && v.lng == null);
        })();
      if (!input || input.value === null || input.value === undefined || pairAllNull) {
        nullCount += 1;
        return;
      }
      items.push({ key: i, engine, value: input.value, dataType: input.dataType, declaration: input.declaration });
    });

    if (items.length === 0) {
      setState({ ...EMPTY, truncated, total: capped.length, nullCount, loading: false });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    void decodeSpatialBatch(items, { signal: controller.signal, maxBytesPerValue: MAX_EAGER_DECODE_BYTES })
      .then((results) => {
        if (cancelled) return;
        const features: SpatialMapFeature[] = [];
        let failedCount = 0;
        let tooLargeCount = 0;
        for (const { key, result } of results) {
          const i = key as number;
          if (result.ok) {
            features.push({ id: i, geometry: result.geometry, properties: buildProperties(capped[i], attachColumnIds) });
          } else if (result.reason === 'too-large') {
            failedCount += 1;
            tooLargeCount += 1;
          } else {
            failedCount += 1;
          }
        }
        setState({ features, truncated, total: capped.length, failedCount, tooLargeCount, nullCount, loading: false });
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof Error && err.name === 'AbortError')) return;
        setState({ ...EMPTY, truncated, total: capped.length, failedCount: items.length, nullCount, loading: false });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // attachKey stands in for attachColumnIds; declKey stands in for the declaration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, engine, geomId, geomType, cap, attachKey, declKey]);

  return state;
}
